import { Button, Paper, Stack, styled, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useEffect, useState } from 'react';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { WorkflowExecutionState } from '../../../data/models/workflow-executions.model';
import { DataGridPro } from '../../tables/DataGridPro';
import { GridColDef } from '@mui/x-data-grid-pro';
import upperFirst from 'lodash/upperFirst';
import dateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
import StepIODialog from './StepIODialog';
import ConfirmationDialog from '../../ConfirmationDialog';

const PREFIX = 'WorkflowExecutionDetails';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  dataRow: `${PREFIX}-dataRow`,
  dataLabel: `${PREFIX}-dataLabel`,
  stepsHeader: `${PREFIX}-stepsHeader`,
  viewButton: `${PREFIX}-viewButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.dataRow}`]: {
    gap: theme.spacing(1),
  },
  [`& .${classes.dataLabel}`]: {
    fontWeight: 'bold',
  },
  [`& .${classes.stepsHeader}`]: {
    marginTop: theme.spacing(2),
  },
  [`& .${classes.viewButton}`]: {
    height: 32,
    width: 100,
  },
}));

export type IOType = 'input' | 'output';

export default function WorkflowExecutionDetails() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { id: workflowExecutionUUID } = useParams();
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [selectedStepName, setSelectedStepName] = useState<string | undefined>(
    undefined,
  );
  const [ioType, setIOType] = useState<IOType>('input');
  const [isIODialogOpen, setIsIODialogOpen] = useState<boolean>(false);
  const [showAbortConfirm, setShowAbortConfirm] = useState<boolean>(false);
  const workflowExecution = useAppSelector(
    monitoringSlice.selectWorkflowExecution,
  );
  const workflow = useAppSelector((state) => {
    return monitoringSlice.selectWorkflow(
      state,
      workflowExecution?.workflowUUID ?? '',
    );
  });
  const abortedWorkflowExecution = useAppSelector(
    monitoringSlice.selectAbortedWorkflowExecution,
  );
  const abortingWorkflowExecution = useAppSelector(
    monitoringSlice.selectAbortingWorkflowExecution,
  );

  const selectedStep = workflowExecution?.steps.find(
    (step) => step.name === selectedStepName,
  );
  const ioDialogContent =
    ioType === 'input' ? selectedStep?.input : selectedStep?.output;

  useEffect(() => {
    if (workflowExecutionUUID) {
      dispatch(
        monitoringSlice.fetchWorkflowExecutionAsync(workflowExecutionUUID),
      );
    }
  }, [workflowExecutionUUID, abortedWorkflowExecution]);

  useEffect(() => {
    if (workflowExecution?.workflowUUID) {
      dispatch(
        monitoringSlice.fetchWorkflowAsync(workflowExecution.workflowUUID),
      );
    }
  }, [workflowExecution]);

  const handleAbortClick = () => {
    setShowAbortConfirm(true);
  };

  const handleAbortExecutionConfirm = () => {
    setShowAbortConfirm(false);
    if (workflowExecution?.workflowExecutionUUID) {
      dispatch(
        monitoringSlice.abortWorkflowExecution(
          workflowExecution.workflowExecutionUUID,
        ),
      );
    }
  };

  const handleViewInputClick = (stepName: string) => {
    setSelectedStepName(stepName);
    setIOType('input');
    setIsIODialogOpen(true);
  };

  const handleViewOutputClick = (stepName: string) => {
    setSelectedStepName(stepName);
    setIOType('output');
    setIsIODialogOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('workflows.name'),
      sortable: true,
      minWidth: 220,
    },
    {
      field: 'status',
      headerName: t('workflows.status'),
      sortable: false,
      valueFormatter: (params) => upperFirst(params.value),
    },
    {
      field: 'beganDateTimeUTC',
      headerName: t('workflows.beginDate'),
      sortable: false,
      minWidth: 160,
      valueFormatter: (params) =>
        dateUtils.formatDateTime(DateTime.fromISO(params.value)),
    },
    {
      field: 'endedDateTimeUTC',
      headerName: t('workflows.endDate'),
      sortable: false,
      minWidth: 160,
      valueFormatter: (params) => {
        if (params.value) {
          return dateUtils.formatDateTime(DateTime.fromISO(params.value));
        }
      },
    },
    {
      field: 'inputAction',
      headerName: '',
      sortable: false,
      minWidth: 140,
      renderCell: (params) => {
        if (params.row.name === hoveredRow && params.row.input) {
          return (
            <Button
              className={classes.viewButton}
              color="primary"
              variant="contained"
              onClick={() => handleViewInputClick(params.row.name)}>
              {t('workflows.viewInput')}
            </Button>
          );
        }
      },
    },
    {
      field: 'outputAction',
      headerName: '',
      sortable: false,
      renderCell: (params) => {
        if (params.row.name === hoveredRow && params.row.output) {
          return (
            <Button
              className={classes.viewButton}
              color="primary"
              variant="contained"
              onClick={() => handleViewOutputClick(params.row.name)}>
              {t('workflows.viewOutput')}
            </Button>
          );
        }
      },
    },
  ];

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="row" className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            {t('workflows.workflowExecutionDetailsTitle', {
              workflowExecutionUUID,
            })}
          </Typography>
          {workflowExecution?.state === WorkflowExecutionState.Active && (
            <Button
              color="primary"
              variant="contained"
              disabled={abortingWorkflowExecution}
              onClick={() => handleAbortClick()}>
              {t('workflows.abort')}
            </Button>
          )}
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('workflows.workflowName')}:
          </Typography>
          <Typography>{workflow?.name}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('workflows.executionID')}:
          </Typography>
          <Typography>{workflowExecution?.workflowExecutionUUID}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('workflows.time')}:
          </Typography>
          <Typography>
            {dateUtils.formatDateTime(
              DateTime.fromISO(workflowExecution?.modifiedDateTimeUTC ?? ''),
            )}
          </Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('workflows.state')}:
          </Typography>
          <Typography>{t(`workflows.${workflowExecution?.state}`)}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('workflows.status')}:
          </Typography>
          <Typography>{t(`workflows.${workflowExecution?.status}`)}</Typography>
        </Stack>
        <Typography className={`${classes.dataLabel} ${classes.stepsHeader}`}>
          {t('workflows.steps')}
        </Typography>
        {workflowExecution?.steps && (
          <DataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.name}
            rows={workflowExecution.steps}
            columns={columns}
            rowCount={workflowExecution.steps.length}
            pagination={false}
            autoHeight
            slotProps={{
              row: {
                onMouseEnter: (event) => {
                  const id = event.currentTarget.dataset.id;
                  setHoveredRow(id);
                },
                onMouseLeave: () => {
                  setHoveredRow(undefined);
                },
              },
            }}
          />
        )}
      </Stack>
      <StepIODialog
        open={isIODialogOpen}
        ioType={ioType}
        content={ioDialogContent ?? {}}
        close={() => setIsIODialogOpen(false)}
      />
      <ConfirmationDialog
        title={t('workflows.confirmAbortTitle')}
        message={t('workflows.confirmAbortMessage')}
        open={showAbortConfirm}
        positiveAction={handleAbortExecutionConfirm}
        negativeAction={() => setShowAbortConfirm(false)}
      />
    </StyledPaper>
  );
}
